/**
 * Shared-DD data layer — DB-backed React Query hooks (reads, mutations, realtime).
 *
 * Replaces the prior Zustand+AsyncStorage-only store: collaborative DD is server
 * state, so it now lives in Postgres (shared_dd / dd_collaborators / dd_notes /
 * dd_note_reactions) and is read/written via React Query, with Supabase Realtime
 * keeping open sessions live. Mirrors the conventions in
 * `src/lib/dd/use-dd-reports.ts` and `src/lib/supabase/realtime.ts`.
 */

import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import { sendDDInvite, acceptDDInvite } from '@/lib/supabase/edge-functions';
import {
  sharedDDFromRows,
  founderSessionFromRow,
  type SharedDDRow,
  type CollaboratorRow,
  type NoteRow,
  type ReactionRow,
} from './shared-dd-mappers';
import type {
  SharedDueDiligence,
  CollaboratorRole,
  NoteCategory,
  ConclusionDecision,
  DDDocumentRef,
} from './types';

// Shared-DD tables aren't in the typed `.from()` union after the enum-superset
// migration; cast the handle (same approach as use-dd-reports.ts).
const db = supabase as any;

const enabledNow = () => isSupabaseConfigured() && !mode.isMock;

export const sharedDDKeys = {
  all: ['shared-dd'] as const,
  mine: (userId: string) => ['shared-dd', 'mine', userId] as const,
  invited: (userId: string) => ['shared-dd', 'invited', userId] as const,
  detail: (sharedDdId: string) => ['shared-dd', 'detail', sharedDdId] as const,
  pendingInvites: (userId: string) => ['shared-dd', 'pending-invites', userId] as const,
  forStartup: (startupId: string) => ['shared-dd', 'for-startup', startupId] as const,
};

// ── Assembly: hydrate session rows with collaborators, notes, reactions ─────
async function hydrateSessions(sessions: SharedDDRow[]): Promise<SharedDueDiligence[]> {
  if (sessions.length === 0) return [];
  const ids = sessions.map((s) => s.id);

  const [{ data: collabs }, { data: notes }] = await Promise.all([
    db.from('dd_collaborators').select('*').in('shared_dd_id', ids),
    db.from('dd_notes').select('*').in('shared_dd_id', ids).order('created_at', { ascending: true }),
  ]);

  const noteRows = (notes ?? []) as NoteRow[];
  const reactionsByNote = new Map<string, ReactionRow[]>();
  if (noteRows.length > 0) {
    const { data: reactions } = await db
      .from('dd_note_reactions')
      .select('*')
      .in('note_id', noteRows.map((n) => n.id));
    for (const r of (reactions ?? []) as ReactionRow[]) {
      const list = reactionsByNote.get(r.note_id) ?? [];
      list.push(r);
      reactionsByNote.set(r.note_id, list);
    }
  }

  const collabRows = (collabs ?? []) as CollaboratorRow[];
  const collabsBySession = new Map<string, CollaboratorRow[]>();
  for (const c of collabRows) {
    const list = collabsBySession.get(c.shared_dd_id) ?? [];
    list.push(c);
    collabsBySession.set(c.shared_dd_id, list);
  }
  const notesBySession = new Map<string, NoteRow[]>();
  for (const n of noteRows) {
    const list = notesBySession.get(n.shared_dd_id) ?? [];
    list.push(n);
    notesBySession.set(n.shared_dd_id, list);
  }

  return sessions.map((s) =>
    sharedDDFromRows(s, collabsBySession.get(s.id) ?? [], notesBySession.get(s.id) ?? [], reactionsByNote),
  );
}

// ── Reads ───────────────────────────────────────────────────────────────────
export function useMySharedDDs(userId: string | undefined) {
  return useQuery({
    queryKey: sharedDDKeys.mine(userId || ''),
    queryFn: async (): Promise<SharedDueDiligence[]> => {
      if (!userId || !enabledNow()) return [];
      const { data, error } = await db
        .from('shared_dd')
        .select('*')
        .eq('owner_id', userId)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return hydrateSessions((data ?? []) as SharedDDRow[]);
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
  });
}

export function useInvitedSharedDDs(userId: string | undefined) {
  return useQuery({
    queryKey: sharedDDKeys.invited(userId || ''),
    queryFn: async (): Promise<SharedDueDiligence[]> => {
      if (!userId || !enabledNow()) return [];
      const { data: memberships, error } = await db
        .from('dd_collaborators')
        .select('shared_dd_id')
        .eq('investor_user_id', userId)
        .eq('status', 'active');
      if (error) throw error;
      const ids = [...new Set(((memberships ?? []) as { shared_dd_id: string }[]).map((m) => m.shared_dd_id))];
      if (ids.length === 0) return [];
      const { data: sessions } = await db
        .from('shared_dd')
        .select('*')
        .in('id', ids)
        .neq('owner_id', userId);
      return hydrateSessions((sessions ?? []) as SharedDDRow[]);
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
  });
}

export function useSharedDD(sharedDdId: string | undefined) {
  return useQuery({
    queryKey: sharedDDKeys.detail(sharedDdId || ''),
    queryFn: async (): Promise<SharedDueDiligence | null> => {
      if (!sharedDdId || !enabledNow()) return null;
      const { data, error } = await db.from('shared_dd').select('*').eq('id', sharedDdId).maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const [hydrated] = await hydrateSessions([data as SharedDDRow]);
      return hydrated ?? null;
    },
    enabled: !!sharedDdId,
    staleTime: 30 * 1000,
  });
}

/**
 * Founder view: the DD sessions running on the founder's own startup. RLS
 * exposes only the session row (lead investor identity + status + dates), never
 * the investors' private notes/conclusions — so this maps via `founderSessionFromRow`
 * (owner as the single visible collaborator, no notes) rather than hydrating.
 * Founders grant data-room access, so they're entitled to know who is diligencing them.
 */
export function useSharedDDsForMyStartup(startupId: string | undefined) {
  return useQuery({
    queryKey: sharedDDKeys.forStartup(startupId || ''),
    queryFn: async (): Promise<SharedDueDiligence[]> => {
      if (!startupId || !enabledNow()) return [];
      const { data, error } = await db
        .from('shared_dd')
        .select('*')
        .eq('startup_id', startupId)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return ((data ?? []) as SharedDDRow[]).map(founderSessionFromRow);
    },
    enabled: !!startupId,
    staleTime: 60 * 1000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────
function useInvalidateSharedDD() {
  const qc = useQueryClient();
  return (sharedDdId?: string) => {
    qc.invalidateQueries({ queryKey: sharedDDKeys.all });
    if (sharedDdId) qc.invalidateQueries({ queryKey: sharedDDKeys.detail(sharedDdId) });
  };
}

interface CreateSharedDDParams {
  startupId: string;
  startupName: string;
  ownerId: string;
  ownerName: string;
  syndicateMode?: boolean;
}

export function useCreateSharedDD() {
  const invalidate = useInvalidateSharedDD();
  return useMutation({
    mutationFn: async (p: CreateSharedDDParams): Promise<string | null> => {
      if (!enabledNow()) return null;
      // Idempotent: reuse an existing active session for this (startup, owner).
      const { data: existing } = await db
        .from('shared_dd')
        .select('id')
        .eq('startup_id', p.startupId)
        .eq('owner_id', p.ownerId)
        .eq('status', 'active')
        .maybeSingle();
      if (existing) return existing.id;

      const { data: session, error } = await db
        .from('shared_dd')
        .insert({
          startup_id: p.startupId,
          startup_name: p.startupName,
          owner_id: p.ownerId,
          owner_name: p.ownerName,
          syndicate_mode: p.syndicateMode ?? false,
        })
        .select('id')
        .single();
      if (error) throw error;

      // Owner joins as an active 'owner' collaborator.
      await db.from('dd_collaborators').insert({
        shared_dd_id: session.id,
        investor_user_id: p.ownerId,
        investor_name: p.ownerName,
        investor_email: '',
        role: 'owner',
        status: 'active',
        joined_at: new Date().toISOString(),
      });
      return session.id;
    },
    onSuccess: () => invalidate(),
    onError: (err) => reportError(err, { context: 'useCreateSharedDD' }),
  });
}

export function useCloseSharedDD() {
  const invalidate = useInvalidateSharedDD();
  return useMutation({
    mutationFn: async (sharedDdId: string) => {
      if (!enabledNow()) return;
      const { error } = await db.from('shared_dd').update({ status: 'closed' }).eq('id', sharedDdId);
      if (error) throw error;
    },
    onSuccess: (_d, sharedDdId) => invalidate(sharedDdId),
    onError: (err) => reportError(err, { context: 'useCloseSharedDD' }),
  });
}

export function useToggleSyndicateMode() {
  const invalidate = useInvalidateSharedDD();
  return useMutation({
    mutationFn: async (p: { sharedDdId: string; syndicateMode: boolean }) => {
      if (!enabledNow()) return;
      const { error } = await db.from('shared_dd').update({ syndicate_mode: p.syndicateMode }).eq('id', p.sharedDdId);
      if (error) throw error;
    },
    onSuccess: (_d, p) => invalidate(p.sharedDdId),
    onError: (err) => reportError(err, { context: 'useToggleSyndicateMode' }),
  });
}

interface AddNoteParams {
  sharedDdId: string;
  authorId: string;
  authorName: string;
  content: string;
  category: NoteCategory;
  isPrivate?: boolean;
  documentRef?: DDDocumentRef;
}

export function useAddNote() {
  const invalidate = useInvalidateSharedDD();
  return useMutation({
    mutationFn: async (p: AddNoteParams) => {
      if (!enabledNow()) return;
      const { error } = await db.from('dd_notes').insert({
        shared_dd_id: p.sharedDdId,
        author_id: p.authorId,
        author_name: p.authorName,
        content: p.content,
        category: p.category,
        is_private: p.isPrivate ?? false,
        document_ref_id: p.documentRef?.documentId ?? null,
        document_ref_name: p.documentRef?.documentName ?? null,
        document_ref_type: p.documentRef?.documentType ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_d, p) => invalidate(p.sharedDdId),
    onError: (err) => reportError(err, { context: 'useAddNote' }),
  });
}

export function useUpdateNote() {
  const invalidate = useInvalidateSharedDD();
  return useMutation({
    mutationFn: async (p: { sharedDdId: string; noteId: string; content: string }) => {
      if (!enabledNow()) return;
      const { error } = await db
        .from('dd_notes')
        .update({ content: p.content, updated_at: new Date().toISOString() })
        .eq('id', p.noteId);
      if (error) throw error;
    },
    onSuccess: (_d, p) => invalidate(p.sharedDdId),
    onError: (err) => reportError(err, { context: 'useUpdateNote' }),
  });
}

export function useDeleteNote() {
  const invalidate = useInvalidateSharedDD();
  return useMutation({
    mutationFn: async (p: { sharedDdId: string; noteId: string }) => {
      if (!enabledNow()) return;
      const { error } = await db.from('dd_notes').delete().eq('id', p.noteId);
      if (error) throw error;
    },
    onSuccess: (_d, p) => invalidate(p.sharedDdId),
    onError: (err) => reportError(err, { context: 'useDeleteNote' }),
  });
}

export function useReactToNote() {
  const invalidate = useInvalidateSharedDD();
  return useMutation({
    mutationFn: async (p: {
      sharedDdId: string;
      noteId: string;
      investorId: string;
      reactionType: 'agree' | 'disagree' | 'important';
    }) => {
      if (!enabledNow()) return;
      // Toggle: remove an identical reaction, otherwise upsert (one per note+investor).
      const { data: existing } = await db
        .from('dd_note_reactions')
        .select('id, reaction_type')
        .eq('note_id', p.noteId)
        .eq('investor_user_id', p.investorId)
        .maybeSingle();
      if (existing?.reaction_type === p.reactionType) {
        const { error } = await db.from('dd_note_reactions').delete().eq('id', existing.id);
        if (error) throw error;
        return;
      }
      const { error } = await db
        .from('dd_note_reactions')
        .upsert(
          { note_id: p.noteId, investor_user_id: p.investorId, reaction_type: p.reactionType },
          { onConflict: 'note_id,investor_user_id' },
        );
      if (error) throw error;
    },
    onSuccess: (_d, p) => invalidate(p.sharedDdId),
    onError: (err) => reportError(err, { context: 'useReactToNote' }),
  });
}

export function useSetConclusion() {
  const invalidate = useInvalidateSharedDD();
  return useMutation({
    mutationFn: async (p: {
      sharedDdId: string;
      investorId: string;
      decision: ConclusionDecision;
      investmentAmount?: number | null;
      notes?: string | null;
    }) => {
      if (!enabledNow()) return;
      const { error } = await db
        .from('dd_collaborators')
        .update({
          conclusion_decision: p.decision,
          conclusion_amount: p.investmentAmount ?? null,
          conclusion_notes: p.notes ?? null,
          conclusion_updated_at: new Date().toISOString(),
        })
        .eq('shared_dd_id', p.sharedDdId)
        .eq('investor_user_id', p.investorId);
      if (error) throw error;
    },
    onSuccess: (_d, p) => invalidate(p.sharedDdId),
    onError: (err) => reportError(err, { context: 'useSetConclusion' }),
  });
}

export function useRemoveCollaborator() {
  const invalidate = useInvalidateSharedDD();
  return useMutation({
    mutationFn: async (p: { sharedDdId: string; collaboratorId: string }) => {
      if (!enabledNow()) return;
      const { error } = await db.from('dd_collaborators').update({ status: 'removed' }).eq('id', p.collaboratorId);
      if (error) throw error;
    },
    onSuccess: (_d, p) => invalidate(p.sharedDdId),
    onError: (err) => reportError(err, { context: 'useRemoveCollaborator' }),
  });
}

export function useInviteCollaborator() {
  const invalidate = useInvalidateSharedDD();
  return useMutation({
    mutationFn: async (p: { sharedDdId: string; email: string; role: CollaboratorRole }) => {
      // Invite issuance + emailing is owned by the `dd-invite` edge function
      // (service role). Map UI roles to the edge fn's accepted set.
      const edgeRole: 'lead' | 'co-investor' | 'observer' =
        p.role === 'collaborator' ? 'co-investor' : p.role === 'viewer' || p.role === 'observer' ? 'observer' : 'lead';
      const res = await sendDDInvite({ sharedDdId: p.sharedDdId, email: p.email, role: edgeRole });
      if (res.error || !res.data?.success) throw new Error(res.error || 'Failed to send invite');
      return res.data;
    },
    onSuccess: (_d, p) => invalidate(p.sharedDdId),
    onError: (err) => reportError(err, { context: 'useInviteCollaborator' }),
  });
}

export function useAcceptInvite() {
  const invalidate = useInvalidateSharedDD();
  return useMutation({
    mutationFn: async (p: { token: string }) => {
      const res = await acceptDDInvite({ token: p.token });
      if (res.error || !res.data?.success) throw new Error(res.error || 'Failed to accept invite');
      return res.data;
    },
    onSuccess: () => invalidate(),
    onError: (err) => reportError(err, { context: 'useAcceptInvite' }),
  });
}

// ── Realtime ────────────────────────────────────────────────────────────────
/**
 * Live updates for an open session: any change to its notes / collaborators /
 * reactions invalidates the session query. RLS gates which rows a subscriber
 * receives. Mirrors `useRealtimeMessages` (subscribe on mount, removeChannel on
 * unmount); the Supabase client handles socket reconnection.
 */
export function useSharedDDRealtime(sharedDdId: string | undefined) {
  const qc = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!sharedDdId || !enabledNow()) return;
    const invalidate = () => qc.invalidateQueries({ queryKey: sharedDDKeys.detail(sharedDdId) });

    const channel = supabase
      .channel(`shared-dd:${sharedDdId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dd_notes', filter: `shared_dd_id=eq.${sharedDdId}` }, invalidate)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dd_collaborators', filter: `shared_dd_id=eq.${sharedDdId}` }, invalidate)
      // Reactions have no shared_dd_id column to filter on; invalidate on any change.
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dd_note_reactions' }, invalidate)
      .subscribe();
    channelRef.current = channel;

    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    };
  }, [sharedDdId, qc]);

  return channelRef.current;
}
